'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ShieldCheck, Phone, AlertTriangle, FileText, ChevronRight } from 'lucide-react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function DisclaimerPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            LEGAL NOTICE
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Legal{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Disclaimer
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Detailed information about our role as an independent booking agent, ticket pricing, and
            airline carrier relationships.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main disclaimer text (Left) */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-5.5 h-5.5 text-accent" />
                Service Disclaimer & Carrier Disclosures
              </h2>

              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                <strong>Faremaps.com</strong> solely acts as an agent, creating a bridge between
                travelers and suppliers of travel services including flight tickets and other travel
                services. We do not own, manage, or operate any kind of Travel Services, aircrafts,
                or hotel rooms.
              </p>

              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                All prices shown are for round trip tickets unless otherwise stated. Displayed fares
                are based on real-time availability and are subject to change. The discount offer is
                applicable to phone bookings.
              </p>

              <div className="p-5 bg-slate-50 border border-slate-200/60 rounded-2xl space-y-4">
                <h3 className="text-slate-800 text-xs font-black uppercase tracking-wider">
                  Independent Agency Statement
                </h3>
                <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                  Faremaps is an independent travel portal and booking desk. We maintain direct
                  wholesale and consolidator agreements with 500+ commercial flight carriers,
                  allowing us to package deals, but we are not affiliated with or endorsed by any
                  specific airline or hospitality provider.
                </p>
              </div>

              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl text-[11px] font-bold text-blue-800 leading-normal">
                🚨 <strong>Price & Fare Fluctuations:</strong> Flight ticket inventories are
                extremely dynamic. Airline ticket prices, route availabilities, baggage rules, and
                boarding times are managed entirely by carrier systems. Faremaps has no authority or
                control over carrier changes or fuel surcharges.
              </div>
            </div>
          </div>

          {/* Sidebar widget (Right) */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    Support Desk
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    For questions about ticket price validation or rebookings, speak to our agents.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Security Guarantee
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      Verified Booking Desk
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      We offer a 100% reservation confirmation pledge directly validated by global
                      carriers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      <Footer />
    </div>
  );
}
